import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

/**
 * Onboarding (2026-10-31, migration 0032).
 * GET /editor/onboarding lazily seeds the "Start here" sample project (ADMIN,
 * once ever) and returns the Get-started checklist state. Every step is derived
 * server-side from real data, so the card ticks itself.
 */
export type OnboardingState =
  | { eligible: false }
  | {
      eligible: true;
      dismissed: boolean;
      complete: boolean;
      steps: { createProject: boolean; upload: boolean; share: boolean; clientOpened: boolean };
      sampleProjectId: string | null;
      sampleVideoId: string | null;
      askSource: boolean;
      justSeeded: boolean;
    };

export const SIGNUP_SOURCE_OPTIONS: { value: string; label: string }[] = [
  { value: "google", label: "Google" },
  { value: "instagram", label: "Instagram" },
  { value: "youtube", label: "YouTube" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "x", label: "X / Twitter" },
  { value: "reddit", label: "Reddit" },
  { value: "friend", label: "A friend" },
  { value: "outreach", label: "Message from the founder" },
  { value: "other", label: "Other" },
];

const KEY = ["onboarding"] as const;

export function useOnboarding(enabled = true) {
  const qc = useQueryClient();
  const q = useQuery<OnboardingState>({
    queryKey: KEY,
    queryFn: () => api.get<OnboardingState>("/editor/onboarding"),
    // Steps change from actions taken on OTHER pages (create project redirects
    // into it, uploads/shares happen inside a project). Always refetch when the
    // Projects page mounts so the card is never stale on return.
    staleTime: 0,
    refetchOnMount: "always",
    enabled,
  });
  // Any successful mutation anywhere (create project, share, etc.) may complete
  // a step — refresh the checklist. Cheap: one small GET, only while mounted.
  useEffect(() => {
    if (!enabled) return;
    return qc.getMutationCache().subscribe((e) => {
      if (e.type === "updated" && e.mutation.state.status === "success" &&
          e.mutation.options.mutationKey?.[0] !== "onboarding") {
        qc.invalidateQueries({ queryKey: KEY });
      }
    });
  }, [enabled, qc]);
  // Sample project was created by this request → the projects list is stale.
  const justSeeded = q.data && q.data.eligible && q.data.justSeeded;
  useEffect(() => {
    if (justSeeded) qc.invalidateQueries({ queryKey: ["projects"] });
  }, [justSeeded, qc]);
  return q;
}

export function useDismissOnboarding() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ["onboarding", "dismiss"],
    mutationFn: () => api.post<{ ok: true }>("/editor/onboarding/dismiss"),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useSetSignupSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ["onboarding", "source"],
    mutationFn: (source: string) => api.post<{ ok: true }>("/editor/onboarding/source", { source }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
